import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import type { CardAttributeQueryParams } from "src/app/features/CardImplementation/subFeatures/CardDefinition/subFeatures/CardAttribute/services/server/CardAttributesQueryTypes";
import { FormManager } from "../../../../../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../../../../../core/setup/redux/state/notifications/notificationsState";
import { useCardAttributeSaveMutation } from "../../services/server/CardAttributeApi";
import type { CardAttributeEntity } from "../../types/CardAttributeEntity";

export const CardAttributeCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("card-attribute");
    const { cardImplementationId, cardDefinitionId } = useParams();
    const [queryConfig, setQueryConfig] = useState<CardAttributeQueryParams>({} as CardAttributeQueryParams);

    useEffect(() => {
        if (cardImplementationId && cardDefinitionId) {
            setQueryConfig({ cardImplementationId, cardDefinitionId });
        }
    }, [cardImplementationId, cardDefinitionId]);
    const [formData, setFormData] = useState<CardAttributeEntity>({} as CardAttributeEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCardAttributeSaveMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData || !queryConfig) {
                return;
            }

            const result = await saveFormData({ cardAttributeFormData: formData, cardAttributeQuery: queryConfig });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "CardAttribute",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(
                    `${BASE_PATH}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${FORM_ACTION.EDIT}/${cardDefinitionId}`,
                );
            }
        },
        [formData, saveFormData, queryConfig, dispatch, navigate, cardImplementationId, cardDefinitionId],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardAttributeEntity>
                    name="CardAttributeCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
